import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useFamiliesByPatient } from '../../hooks/api';
import apiClient from '../../lib/api';
import { PageDataBoundary } from '../../components/PageDataBoundary/PageDataBoundary';
import { PageContainer, PageHeading, TextButton } from '../../components';

// Styled Components

const UserCard = styled.div`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.md};
  gap: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const UserAvatar = styled.div`
  width: 48px;
  height: 48px;
  background: ${({ theme }) => theme.colors.pageBackground};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const AvatarIcon = styled.span`
  font-size: 22px;
`;

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const UserLineId = styled.h2`
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  line-height: 150%;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 2px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UserRelationship = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`;

const Section = styled.div`
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: ${({ theme }) => theme.spacing.sm} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  gap: ${({ theme }) => theme.spacing.md};

  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  min-width: 80px;
`;

const DetailValue = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  text-align: right;
  flex: 1;
  word-break: break-word;
`;

const DangerButton = styled.button`
  width: 100%;
  min-height: ${({ theme }) => theme.accessibility.minTouchTarget};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.ms};
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.status.error};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  line-height: 150%;
  color: ${({ theme }) => theme.colors.status.error};
  text-align: center;
  transition: opacity 0.2s;

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${({ theme }) => theme.spacing.md};
`;

const ModalContent = styled.div`
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.xl};
  max-width: 400px;
  width: 100%;
  box-shadow: ${({ theme }) => theme.shadows.lg};
`;

const ModalTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
`;

const ModalText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0 0 ${({ theme }) => theme.spacing.lg} 0;
  line-height: 1.6;
`;

const ModalWarning = styled.div`
  background: ${({ theme }) => theme.colors.backgroundTableWarning};
  border: 1px solid ${({ theme }) => theme.colors.status.warning};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  margin: 0 0 ${({ theme }) => theme.spacing.lg} 0;
`;

const ModalWarningText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  line-height: 1.5;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
`;

const ModalButton = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  flex: 1;
  min-height: ${({ theme }) => theme.accessibility.minTouchTarget};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.ms};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.md};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  line-height: 150%;
  cursor: pointer;
  transition: opacity 0.2s;

  ${({ $variant, theme }) => {
    if ($variant === 'danger') {
      return `
        background: ${theme.colors.status.error};
        border: 1px solid ${theme.colors.status.error};
        color: ${theme.colors.text.inverse};
        &:hover {
          opacity: 0.85;
        }
      `;
    } else if ($variant === 'primary') {
      return `
        background: ${theme.colors.primary};
        border: 1px solid ${theme.colors.primary};
        color: ${theme.colors.text.inverse};
        &:hover {
          opacity: 0.85;
        }
      `;
    } else {
      return `
        background: ${theme.colors.backgroundSecondary};
        border: 1px solid ${theme.colors.border.medium};
        color: ${theme.colors.text.secondary};
        &:hover {
          opacity: 0.85;
        }
      `;
    }
  }}

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ModalErrorMessage = styled.p`
  color: ${({ theme }) => theme.colors.status.error};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin: ${({ theme }) => theme.spacing.xs} 0 ${({ theme }) => theme.spacing.md};
`;

export const FamilyDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { lineUserId, patientId } = useParams<{ lineUserId: string; patientId: string }>();

  const [isUnlinkModalOpen, setIsUnlinkModalOpen] = useState(false);
  const [unlinkError, setUnlinkError] = useState('');

  const patientIdNum = patientId ? parseInt(patientId, 10) : null;
  const { data: familiesData, isLoading, error } = useFamiliesByPatient(patientIdNum);

  const unlinkMutation = useMutation({
    mutationFn: () => {
      if (!patientIdNum || !lineUserId) {
        throw new Error('患者IDまたはLINEユーザーIDが指定されていません');
      }
      return apiClient.unlinkFamily(patientIdNum, lineUserId);
    },
    onSuccess: () => {
      // キャッシュを無効化して再取得
      queryClient.invalidateQueries({ queryKey: ['familiesByPatient'] });
      queryClient.invalidateQueries({ queryKey: ['linkedPatients'] });
      // 家族一覧ページに戻る
      navigate('/menu/families');
    },
    onError: (error: any) => {
      setUnlinkError(error.message || '連携解除に失敗しました');
    },
  });

  const handleBack = () => {
    navigate('/menu/families');
  };

  const handleUnlinkClick = () => {
    setUnlinkError('');
    setIsUnlinkModalOpen(true);
  };

  const handleConfirmUnlink = () => {
    unlinkMutation.mutate();
  };

  // 特定の家族メンバーを取得
  const familyMember = familiesData?.families.find(
    (f) => f.line_user_id === lineUserId && f.patient_id === patientIdNum
  );

  const formatDate = (dateString?: string) => {
    if (!dateString) return '不明';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('ja-JP', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  // ローディング中は PageDataBoundary でスピナー表示（本文は描画しない）
  if (isLoading) {
    return <PageDataBoundary ready={false}><></></PageDataBoundary>;
  }

  // 取得失敗・対象家族未発見時のエラー表示
  if (error || !familyMember) {
    return (
      <PageContainer>
        <PageHeading>家族詳細</PageHeading>
        <ErrorMessage>
          家族情報が見つかりませんでした
        </ErrorMessage>
        <TextButton onClick={handleBack} aria-label="戻る">
          戻る
        </TextButton>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeading>家族詳細</PageHeading>

      <UserCard>
        <UserAvatar>
          <AvatarIcon>👤</AvatarIcon>
        </UserAvatar>
        <UserInfo>
          <UserLineId>{familyMember.display_name || familyMember.line_user_id}</UserLineId>
          <UserRelationship>{familyMember.relationship || '続柄未設定'}</UserRelationship>
        </UserInfo>
      </UserCard>

      <Section>
        <SectionTitle>連携情報</SectionTitle>
        <DetailRow>
          <DetailLabel>利用者名</DetailLabel>
          <DetailValue>{familiesData?.patient.name || '不明'}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>続柄</DetailLabel>
          <DetailValue>{familyMember.relationship || '未設定'}</DetailValue>
        </DetailRow>
      </Section>

      <Section>
        <SectionTitle>登録情報</SectionTitle>
        <DetailRow>
          <DetailLabel>連携日時</DetailLabel>
          <DetailValue>{formatDate(familyMember.created_at)}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>更新日時</DetailLabel>
          <DetailValue>{formatDate(familyMember.updated_at)}</DetailValue>
        </DetailRow>
      </Section>

      <Section>
        <DangerButton onClick={handleUnlinkClick} disabled={unlinkMutation.isPending}>
          {unlinkMutation.isPending ? '解除中...' : '連携を解除'}
        </DangerButton>
      </Section>

      <TextButton onClick={handleBack} aria-label="戻る">
        戻る
      </TextButton>

      {/* 連携解除確認モーダル */}
      {isUnlinkModalOpen && (
        <ModalOverlay onClick={() => !unlinkMutation.isPending && setIsUnlinkModalOpen(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>連携解除の確認</ModalTitle>
            <ModalText>
              <strong>{familyMember.display_name || familyMember.line_user_id}</strong> さんと
              <strong>{familiesData?.patient.name || '利用者'}</strong> さんの連携を解除しますか?
            </ModalText>
            <ModalWarning>
              <ModalWarningText>
                ⚠️ この操作は取り消せません。連携を解除すると、この家族メンバーは利用者の情報にアクセスできなくなります。
              </ModalWarningText>
            </ModalWarning>
            {unlinkError && <ModalErrorMessage>{unlinkError}</ModalErrorMessage>}
            <ModalButtons>
              <ModalButton
                $variant="secondary"
                onClick={() => setIsUnlinkModalOpen(false)}
                disabled={unlinkMutation.isPending}
              >
                キャンセル
              </ModalButton>
              <ModalButton
                $variant="danger"
                onClick={handleConfirmUnlink}
                disabled={unlinkMutation.isPending}
              >
                {unlinkMutation.isPending ? '解除中...' : '連携を解除'}
              </ModalButton>
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}
    </PageContainer>
  );
};

export default FamilyDetailPage;
